import React, { useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD } from './adminStyles';

/**
 * Permanent project deletion, behind a typed confirmation.
 *
 * **23 Sep 2026: this stopped being its own disclosure.** It used to sit under
 * the settings row behind a "Project Options" link with its own rule and its
 * own heading — two headings and two sections for what is, in practice, one
 * short row of occasional actions. Tom asked for the lot on one line, so the
 * button now lives in ProjectAdmin's settings row and this file keeps only the
 * confirmation.
 *
 * The disclosure was never the safety feature and hiding it was never the
 * point — **typing the project's name is the guard**, and that is untouched.
 * Archive remains the normal action for real work; this is for test, duplicate
 * or accidentally created projects.
 *
 * **THE GUARD ASKED FOR SOMETHING IT WOULD NOT ACCEPT. 17 Oct 2026.**
 *
 * The project's name was printed inside the `.label` span — 10px tracked
 * CAPITALS — so a project called *Money Talk* was instructed as *TYPE THE
 * PROJECT NAME TO CONFIRM · MONEY TALK*. The comparison was case-sensitive.
 * Tom typed exactly what the screen told him to, and Delete Permanently
 * stayed disabled with nothing to explain why. A guard that cannot be
 * satisfied by following its own instruction is not a guard, it is a dead
 * end.
 *
 * Two changes, and both are needed:
 *
 *   THE NAME IS SHOWN AS IT IS WRITTEN. Out of the caps run and onto its own
 *   line in the project's real case. This is the typography law in
 *   claude/portal-os-visual-rules.md — page titles and client organisations
 *   are set in capitals, *project names are printed as they are written* —
 *   and breaking it here had a consequence beyond looking wrong.
 *
 *   THE MATCH IGNORES CASE. Nobody is protected by case-sensitivity: a person
 *   who types the right words has demonstrated the intent the guard exists to
 *   test, and capitalising one of them is not evidence of a mistake. Trimmed
 *   and lower-cased on both sides.
 */
export function DeleteProjectPanel({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    activeProject,
    deleteProject
  } = usePortal();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const matches = typed.trim().toLowerCase() === activeProject.name.trim().toLowerCase() && activeProject.name.trim().length > 0;
  return <div className="mt-8 max-w-xl border border-paper/30 px-6 py-6" data-id="element-1238" data-mp="1oh9s96eolf9a">
      <p className="font-display text-2xl leading-snug" data-id="element-1239" data-mp="1khj0p0hbyv3p">Permanently delete this project?</p>
      <p className="label-sm mt-4 leading-relaxed text-paper/40" data-id="element-1240" data-mp="1y8xnlieczgg1">
        Use this only for test, duplicate or accidentally created projects. This cannot be undone.
      </p>

      <label className="mt-7 block" data-id="element-1241" data-mp="286tt9uoj1j6r">
        <span className="label block text-paper/40" data-id="element-1242" data-mp="287vayi01t3xg">Type the project name to confirm</span>
        {/* Mixed case, deliberately. See the note at the top of this file. */}
        <span className="mt-2 block font-display text-xl font-light leading-none text-paper" data-id="element-1243" data-mp="2mvn7l9i7ufs7">
          {activeProject.name}
        </span>
        <input value={typed} onChange={event => setTyped(event.target.value)} autoFocus className={FIELD} data-id="element-1244" data-mp="2qtkxkejt95wn" />
      </label>

      <div className="mt-7 flex flex-wrap items-center gap-6" data-id="element-1245" data-mp="2hto47zmh7iv9">
        <button type="button" disabled={!matches || busy} onClick={async () => {
        if (!matches || busy) return;
        setBusy(true);
        const done = await deleteProject();
        setBusy(false);
        if (done) onClose();
      }} className={`${BUTTON_SOLID} disabled:border-paper/25 disabled:bg-transparent disabled:text-paper/30`} data-id="element-1246" data-mp="235tcomk7fkjr">
          {busy ? 'Deleting' : 'Delete Permanently'}
        </button>
        <button type="button" onClick={onClose} className={BUTTON_QUIET} data-id="element-1247" data-mp="1p6ab6clwf7gf">
          Cancel
        </button>
      </div>

      <p className="label-sm mt-6 leading-relaxed text-paper/30" data-id="element-1248" data-mp="2sgftl8pi1ppl">
        The project, its deliverables, client feedback and its client link are removed. No other
        project is affected, and nothing is deleted from Google Drive.
      </p>
    </div>;
}